import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  QrCode, BarChart3, MousePointerClick, ArrowRight, Sparkles,
  Plus, Download, UserPen, Eye, Crown, TrendingUp, Package,
} from "lucide-react";

export default function DashboardOverview() {
  const [profile, setProfile] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [variants, setVariants] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [topPreview, setTopPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, variantsRes] = await Promise.all([
          api.get("/profile"),
          api.get("/variants"),
        ]);
        setProfile(profileRes.data);
        setVariants(variantsRes.data || []);
        try {
          const analyticsRes = await api.get("/analytics");
          setAnalytics(analyticsRes.data);
        } catch {
          setAnalytics(null);
        }
        try {
          const compRes = await api.get("/analytics/comparison");
          setComparison(compRes.data?.variants || []);
        } catch {
          setComparison([]);
        }
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Load top variant preview image
  useEffect(() => {
    if (comparison.length > 0) {
      const top = comparison[0]; // already sorted by scans desc
      api.get(`/variants/${top.id}/image?format=png&size=6&base_url=${encodeURIComponent(BACKEND_URL)}`)
        .then((res) => setTopPreview(res.data?.data || null))
        .catch(() => {});
    }
  }, [comparison]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="animate-fade-in" data-testid="dashboard-no-profile">
        <h1 className="font-heading text-2xl font-bold mb-2">Welcome to Smart-QR</h1>
        <p className="text-muted-foreground mb-8">Let's get you set up. Start by creating your profile.</p>
        <Card className="card-hover border-dashed border-2 border-primary/30 bg-primary/5">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <QrCode className="text-primary mb-4" size={48} strokeWidth={1.5} />
            <h3 className="font-heading text-lg font-semibold mb-2">Create Your Profile</h3>
            <p className="text-muted-foreground text-sm mb-6 text-center max-w-sm">
              Set up your username and profile info to get your Smart-QR code.
            </p>
            <Link to="/dashboard/profile">
              <Button className="btn-glow" data-testid="create-profile-btn">
                Get Started <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalScans = analytics?.total_scans || 0;
  const totalClicks = analytics?.total_clicks || 0;
  const topVariant = comparison.length > 0 ? comparison[0] : null;

  return (
    <div className="animate-fade-in space-y-8" data-testid="dashboard-overview">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Dashboard</h1>
        <p className="text-muted-foreground text-sm">
          Welcome back, <span className="text-foreground font-medium">@{profile.username}</span>
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stats-grid">
        {/* Total Scans */}
        <Card className="card-hover overflow-hidden" data-testid="stat-total-scans">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-primary/10">
                <QrCode size={20} className="text-primary" strokeWidth={1.8} />
              </div>
              {totalScans > 0 && (
                <Badge variant="secondary" className="text-[10px] gap-1 font-normal">
                  <TrendingUp size={10} /> Active
                </Badge>
              )}
            </div>
            <p className="text-3xl font-heading font-bold tracking-tight">{totalScans}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Scans</p>
          </CardContent>
        </Card>

        {/* Total Clicks */}
        <Card className="card-hover overflow-hidden" data-testid="stat-total-clicks">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-violet-500/10">
                <MousePointerClick size={20} className="text-violet-500" strokeWidth={1.8} />
              </div>
            </div>
            <p className="text-3xl font-heading font-bold tracking-tight">{totalClicks}</p>
            <p className="text-xs text-muted-foreground mt-1">Link Clicks</p>
          </CardContent>
        </Card>

        {/* Top Variant */}
        <Card className="card-hover overflow-hidden" data-testid="stat-top-variant">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2.5 rounded-xl bg-amber-500/10">
                <Crown size={20} className="text-amber-500" strokeWidth={1.8} />
              </div>
            </div>
            {topVariant ? (
              <div className="flex items-center gap-3">
                {topPreview && (
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-white border border-border/40 p-1 overflow-hidden" data-testid="top-variant-preview">
                    <img src={topPreview} alt={topVariant.name} className="w-full h-full object-contain" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{topVariant.name}</p>
                  <p className="text-xs text-muted-foreground">{topVariant.total_scans} scans</p>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground">No variants yet</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">Top Variant</p>
          </CardContent>
        </Card>
      </div>

      {/* Complete Setup Reminder */}
      {(variants.length === 0 || !profile.phone) && (
        <Card className="border-dashed border-2 border-primary/30 bg-primary/5" data-testid="complete-setup-card">
          <CardContent className="flex items-center gap-4 py-5">
            <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
              <Sparkles size={22} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Complete Your Setup</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {variants.length === 0 ? "Create your first QR variant to start sharing." : "Add contact info so visitors can save your details."}
              </p>
            </div>
            <Link to={variants.length === 0 ? "/dashboard/qrcode" : "/dashboard/profile"}>
              <Button size="sm" className="btn-glow shrink-0" data-testid="complete-setup-btn">
                {variants.length === 0 ? "Create QR" : "Add Info"} <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions + Recent Scans */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <Card className="card-hover" data-testid="quick-actions-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Sparkles size={16} className="text-primary" /> Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/dashboard/qrcode">
              <Button variant="secondary" className="w-full justify-between group" data-testid="quick-create-variant">
                <span className="flex items-center gap-2"><Plus size={15} /> Create Variant</span>
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
            <Link to="/dashboard/qrcode">
              <Button variant="secondary" className="w-full justify-between group" data-testid="quick-download-pack">
                <span className="flex items-center gap-2"><Package size={15} /> Download QR Pack</span>
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
            <Link to="/dashboard/profile">
              <Button variant="secondary" className="w-full justify-between group" data-testid="quick-edit-profile">
                <span className="flex items-center gap-2"><UserPen size={15} /> Edit Profile</span>
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
            <Link to="/dashboard/analytics">
              <Button variant="secondary" className="w-full justify-between group" data-testid="quick-analytics">
                <span className="flex items-center gap-2"><BarChart3 size={15} /> View Analytics</span>
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Scans */}
        <Card className="card-hover" data-testid="recent-scans-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <QrCode size={16} className="text-primary" /> Recent Scans
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.scans?.length > 0 ? (
              <div className="space-y-3">
                {analytics.scans.slice(0, 5).map((scan) => (
                  <div key={scan.id} className="flex items-center justify-between text-sm py-1.5 px-2 rounded-lg hover:bg-secondary/40 transition-colors" data-testid={`scan-item-${scan.id}`}>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="text-[10px] font-normal">{scan.device_type}</Badge>
                      <span className="text-muted-foreground text-xs">{scan.browser} / {scan.os}</span>
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(scan.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                ))}
                <Link to="/dashboard/analytics" className="block">
                  <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground gap-1 mt-1" data-testid="view-all-scans-btn">
                    View All <ArrowRight size={12} />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-8" data-testid="no-scans-message">
                <QrCode className="mx-auto text-muted-foreground/30 mb-3" size={32} />
                <p className="text-sm text-muted-foreground">No scans yet. Share your QR code to start tracking.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Variant Performance Overview (if >1 variant) */}
      {comparison.length > 1 && (
        <Card className="card-hover" data-testid="variant-performance-card">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <TrendingUp size={16} className="text-primary" /> Variant Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {comparison.slice(0, 5).map((v, i) => {
                const maxScans = comparison[0]?.total_scans || 1;
                const pct = maxScans > 0 ? (v.total_scans / maxScans) * 100 : 0;
                return (
                  <div key={v.id} className="flex items-center gap-3 py-1.5" data-testid={`perf-variant-${v.id}`}>
                    <span className="text-xs text-muted-foreground w-4 shrink-0">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium truncate">{v.name}</span>
                        <span className="text-xs text-muted-foreground shrink-0 ml-2">{v.total_scans} scans</span>
                      </div>
                      <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${Math.max(pct, 2)}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
